import { logger } from '@vagabondinternet/logging';
import { RETURN_TO_PARAM } from '@words-happen/app-urls';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  authErrorCode,
  browserIdentitySession,
  browserSignInLinkEmailStore,
  type EmailLinkSignIn,
  type IdentitySession,
  identityPlatformEmailLink,
  type SignInLinkEmailStore,
  signInLinkFailure,
} from '../../api';
import { type BrowserNavigation, useAuthNavigation } from '../../navigation';
import { ROUTES } from '../../routes';
import { HandoffScreen, useSessionHandoff } from '../../session';
import { ACTION_CODE_PARAM } from '../verify-email-confirm/verify-email-confirm-page';
import { SIGN_IN_LINK_MESSAGES, signInLinkErrorMessage } from './sign-in-link-messages';
import { SignInLinkScreen, type SignInLinkState } from './sign-in-link-screen';
import { SignInLinkSentScreen } from './sign-in-link-sent-screen';
import { useSignInLinkRequest } from './use-sign-in-link-request';

/** The parameter an action link carries its continue URL in, when it isn't redirected to it first. */
export const CONTINUE_URL_PARAM = 'continueUrl';

/**
 * The query string with return_to lifted out of continueUrl, or undefined when return_to is already there or there
 * is none to lift. Firebase's action handler redirects to the continue URL, so return_to arrives at the top level;
 * ENG-852's /auth/action forwards the link's query untouched, leaving it inside continueUrl.
 */
export const liftedReturnToSearch = (params: URLSearchParams): string | undefined => {
  const continueUrl = params.get(CONTINUE_URL_PARAM);
  if (params.has(RETURN_TO_PARAM) || !continueUrl) {
    return undefined;
  }
  let returnTo: string | null;
  try {
    returnTo = new URL(continueUrl).searchParams.get(RETURN_TO_PARAM);
  } catch {
    return undefined;
  }
  if (!returnTo) {
    return undefined;
  }
  const lifted = new URLSearchParams(params);
  lifted.set(RETURN_TO_PARAM, returnTo);
  return `?${lifted}`;
};

export interface SignInLinkPageProps {
  /** Identity Platform's email link sign-in. Defaults to the browser's, loading Firebase on first use. */
  emailLink?: EmailLinkSignIn;
  /** Where this device remembers the address it asked for a link with. Defaults to localStorage. */
  emailStore?: SignInLinkEmailStore;
  /** Firebase's signed-in user and the API's session endpoints. Defaults to the browser's. */
  session?: IdentitySession;
  /** How a signed-in player leaves the app. Defaults to the window's history and location. */
  navigation?: BrowserNavigation;
}

type SignInLinkFlowProps = Required<Omit<SignInLinkPageProps, 'navigation'>> & Pick<SignInLinkPageProps, 'navigation'>;

const SignInLinkFlow: React.FC<SignInLinkFlowProps> = ({ emailLink, emailStore, session, navigation }) => {
  const { switchTo, leave, returnTo, continueUrlTo } = useAuthNavigation(navigation);
  const { pathname, search } = useLocation();
  // The link as the player opened it: Firebase reads its oobCode, mode and apiKey.
  const [link] = useState(() => new URL(`${pathname}${search}`, IDENTITY_URL).href);
  const [state, setState] = useState<SignInLinkState>('checking');
  const [email, setEmail] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [emailError, setEmailError] = useState<string>();
  // React StrictMode mounts twice in development; the link is used once per attempt.
  const started = useRef(false);

  const toVerifyEmail = useCallback(
    (address: string | undefined) =>
      switchTo(ROUTES.verifyEmail, address ? { email: address } : undefined, { replace: true }),
    [switchTo],
  );
  const onUnverified = useCallback(() => toVerifyEmail(email), [toVerifyEmail, email]);
  const handoff = useSessionHandoff({ session, returnTo, leave, onUnverified });
  const request = useSignInLinkRequest({
    emailLink,
    emailStore,
    continueUrl: continueUrlTo(ROUTES.signInLink),
  });

  const toSignIn = () => switchTo(ROUTES.signIn, email ? { email } : undefined);

  /** Signs in with the link and hands off as /sign-in does; otherwise shows why it can't be used. */
  const signIn = useCallback(
    async (address: string, typed: boolean) => {
      try {
        const { verified } = await emailLink.signInWithLink(address, link);
        emailStore.clear();
        if (verified) {
          await handoff.complete();
        } else {
          toVerifyEmail(address);
        }
      } catch (cause) {
        logger.error({ message: 'Signing in with an email link failed', code: authErrorCode(cause) });
        const failure = signInLinkFailure(cause);
        if (failure === 'wrong-email') {
          setEmailError(SIGN_IN_LINK_MESSAGES.wrongEmail);
          setState('email');
        } else if (failure) {
          setState(failure);
        } else {
          setError(signInLinkErrorMessage(cause, SIGN_IN_LINK_MESSAGES.signInFailed));
          setState(typed ? 'email' : 'failed');
        }
      }
    },
    [emailLink, emailStore, link, handoff.complete, toVerifyEmail],
  );

  /** Checks the link, then signs in with the address stored on this device, or asks for it. */
  const start = useCallback(async () => {
    setState('checking');
    setError(undefined);
    setEmailError(undefined);
    // A link without a code can't sign anyone in: say so without loading Firebase.
    if (!new URL(link).searchParams.get(ACTION_CODE_PARAM)) {
      setState('invalid');
      return;
    }
    let isLink: boolean;
    try {
      isLink = await emailLink.isSignInLink(link);
    } catch (cause) {
      logger.error({ message: 'Checking the sign-in link failed', code: authErrorCode(cause) });
      setError(signInLinkErrorMessage(cause, SIGN_IN_LINK_MESSAGES.checkFailed));
      setState('failed');
      return;
    }
    if (!isLink) {
      setState('invalid');
      return;
    }
    const stored = emailStore.read();
    if (!stored) {
      setState('email');
      return;
    }
    setEmail(stored);
    await signIn(stored, false);
  }, [emailLink, emailStore, link, signIn]);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;
    void start();
  }, [start]);

  const submitEmail = async (address: string) => {
    setEmail(address);
    setSubmitting(true);
    setError(undefined);
    setEmailError(undefined);
    await signIn(address, true);
    setSubmitting(false);
  };

  /** A new link to the address in use; with none known, /forgot-password offers one. */
  const requestNewLink = () => (email ? void request.request(email) : switchTo(ROUTES.forgotPassword));

  if (handoff.state.phase !== 'idle') {
    return (
      <HandoffScreen
        handoff={{
          ...handoff,
          // The link is used up: "Sign in again" and "Back to sign in" sign out and continue on /sign-in.
          startOver: async () => {
            await handoff.startOver();
            toSignIn();
          },
        }}
      />
    );
  }

  if (request.sentTo) {
    return <SignInLinkSentScreen email={request.sentTo} {...request.sentScreen} onBack={toSignIn} />;
  }

  return (
    <SignInLinkScreen
      state={state}
      email={email}
      submitting={submitting}
      error={error}
      emailError={emailError}
      newLink={request.resend}
      onSubmitEmail={(address) => void submitEmail(address)}
      onRetry={() => void start()}
      onRequestNewLink={requestNewLink}
      onSignIn={toSignIn}
    />
  );
};

/**
 * /sign-in/link?oobCode=…&mode=signIn&apiKey=…&return_to=…: signs in with an email link (isSignInWithEmailLink, then
 * signInWithEmailLink) using the address this device asked for it with, or the one the player types when it was
 * opened elsewhere. The stored address is then cleared and the player is handed off as on /sign-in: to the validated
 * return_to, or GAME_HOME_URL. Signing in with a link verifies the address. An expired, used or invalid link offers a new
 * one. Works with the link redirected by Firebase's action handler and with the query forwarded untouched, where
 * return_to is lifted out of continueUrl first.
 */
export const SignInLinkPage: React.FC<SignInLinkPageProps> = ({
  emailLink = identityPlatformEmailLink,
  emailStore = browserSignInLinkEmailStore,
  session = browserIdentitySession,
  navigation,
}) => {
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const lifted = liftedReturnToSearch(params);

  if (lifted) {
    return <Navigate replace to={{ pathname, search: lifted }} />;
  }

  return <SignInLinkFlow emailLink={emailLink} emailStore={emailStore} session={session} navigation={navigation} />;
};
